import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { 
  Bell, X, Check, CheckCheck, Heart, MessageCircle, 
  Users, Trophy, ChefHat, Loader2
} from 'lucide-react';

import { useUnreadCount, useNotifications, useMarkAllNotificationsRead } from '../lib/queries';
import { useQueryClient } from '@tanstack/react-query';
import usePushBus from '../hooks/usePushBus';

// Notification type icons
const NOTIF_ICONS = {
  reaction: Heart,
  comment: MessageCircle,
  join: Users,
  badge: Trophy,
  challenge_post: ChefHat,
};

// Notification type colors
const NOTIF_COLORS = {
  reaction: 'text-red-500 bg-red-500/10',
  comment: 'text-blue-500 bg-blue-500/10',
  join: 'text-green-500 bg-green-500/10',
  badge: 'text-yellow-500 bg-yellow-500/10',
  challenge_post: 'text-pink-500 bg-pink-500/10',
};

// Helper to format notification message with translation
const formatNotificationMessage = (message, t) => {
  // Check if it's a badge unlock message (format: badge_unlock:code:emoji)
  if (message && message.startsWith('badge_unlock:')) {
    const parts = message.split(':');
    if (parts.length >= 3) {
      const badgeCode = parts[1];
      const emoji = parts[2];
      const badgeName = t(`badgeNames.${badgeCode}`) || badgeCode;
      return `${t('notifications.badgeUnlocked')} ${emoji} ${badgeName}!`;
    }
  }
  return message;
};

export default function NotificationBell() {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const { t, language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const bellRef = useRef(null);
  const qc = useQueryClient();

  // Polled unread badge count (every 30s automatically)
  const { data: unreadData } = useUnreadCount({ enabled: !!user });
  const unreadCount = unreadData?.count || 0;

  // Full list only when panel is open (auto-fetch on language change via query key)
  const { data: notificationsData = [], isLoading: loading } = useNotifications({ enabled: !!user && isOpen });
  const notifications = notificationsData;

  const markReadMut = useMarkAllNotificationsRead();

  // Allow other components (e.g. <InteractionsBanner /> on the home
  // page) to open this panel programmatically. They dispatch a
  // `slimmy:open-bell` window event; we open the panel AND mark all
  // unread notifications as read so the dispatching banner self-
  // hides (its visibility is driven by the same unread-count query).
  useEffect(() => {
    const onOpen = () => {
      setIsOpen(true);
      document.body.style.overflow = 'hidden';
      // Fire-and-forget: best-effort mark-all-read so banners and
      // badges collapse as the panel opens. If the user has nothing
      // unread the mutation is a cheap no-op.
      markReadMut.mutate(undefined, { onError: () => {} });
    };
    window.addEventListener('slimmy:open-bell', onOpen);
    return () => window.removeEventListener('slimmy:open-bell', onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live refresh: when a push arrives (admin reply, like, comment,
  // badge…) immediately refetch the unread-count so the red dot
  // appears in the same second instead of waiting up to 30 s for
  // the next React-Query poll. If the panel is open, also refetch
  // the full list so the new entry shows up at the top.
  const onPush = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    if (isOpen) {
      qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    }
  }, [qc, isOpen]);
  usePushBus(onPush);

  // Re-fetch notifications list when language changes so server-side translations update
  useEffect(() => {
    if (isOpen && user) {
      qc.invalidateQueries({ queryKey: ['notifications', 'list'] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const handleOpen = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const markAllAsRead = () => markReadMut.mutate();

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return t('time.now');
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}${t('common.days').charAt(0)}`;
    
    const locale = language === 'it' ? 'it-IT' : language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : language === 'de' ? 'de-DE' : language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  if (!user) return null;

  return (
    <>
      {/* Bell Button */}
      <button
        ref={bellRef}
        onClick={handleOpen}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          isDark 
            ? 'bg-white/5 hover:bg-white/10' 
            : 'bg-gray-100 hover:bg-gray-200'
        }`}
        data-testid="notification-bell"
      >
        <Bell className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
        
        {/* Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1.5 rounded-full bg-[#E8334A] text-white text-xs font-bold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Panel - Portal */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[100]" data-testid="notification-panel">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Panel */}
          <div 
            className={`absolute right-0 top-0 h-full w-full max-w-md flex flex-col ${
              isDark ? 'bg-[#0A0F1C]' : 'bg-white'
            } shadow-2xl animate-slide-in-right`}
          >
            {/* Header */}
            <div 
              className={`sticky top-0 z-10 px-4 pb-4 border-b ${
                isDark ? 'bg-[#0A0F1C] border-white/5' : 'bg-white border-gray-100'
              }`}
              style={{ paddingTop: 'max(24px, env(safe-area-inset-top, 24px))' }}
            >
              <div className="flex items-center justify-between">
                <h2 className={`font-display text-xl ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('notifications.title')}
                </h2>
                <div className="flex items-center gap-2">
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                        isDark 
                          ? 'bg-white/5 text-white/70 hover:bg-white/10' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <CheckCheck className="w-4 h-4" />
                      {t('notifications.read')}
                    </button>
                  )}
                  <button
                    onClick={handleClose}
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <X className={`w-5 h-5 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            <div className="flex-1 overflow-y-auto" style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 20px))' }}>
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 animate-spin text-[#E8334A]" />
                </div>
              ) : notifications.length === 0 ? (
                <div className={`text-center py-20 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  <Bell className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">{t('notifications.empty')}</p>
                  <p className="text-sm mt-1">{t('notifications.emptySubtitle')}</p>
                </div>
              ) : (
                <div className="divide-y divide-white/5">
                  {notifications.map((notif, idx) => {
                    const Icon = NOTIF_ICONS[notif.type] || Bell;
                    const colorClass = NOTIF_COLORS[notif.type] || 'text-gray-500 bg-gray-500/10';
                    
                    return (
                      <div
                        key={notif.notification_id || `notif-${idx}`}
                        className={`px-4 py-4 flex gap-3 transition-colors ${
                          !notif.read 
                            ? isDark ? 'bg-white/5' : 'bg-blue-50/50'
                            : ''
                        }`}
                      >
                        {/* Icon */}
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        
                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatNotificationMessage(notif.message, t)}
                          </p>
                          <p className={`text-xs mt-1 ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                            {formatTime(notif.created_at)}
                          </p>
                        </div>
                        
                        {/* Unread indicator */}
                        {!notif.read && (
                          <div className="w-2 h-2 rounded-full bg-[#E8334A] flex-shrink-0 mt-2" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* CSS for animation */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
